/**
 * Bayesian Expected A Posteriori (EAP) Ability Estimator
 *
 * Replaces the fixed-step delta-rule with proper Bayesian estimation.
 * Uses numerical integration over a discretized theta grid.
 *
 * Key advantages over delta rule:
 * - Converges: posterior variance shrinks with each response (~1/√n)
 * - Cold-start safe: wide N(0,1) prior handles new users gracefully
 * - Outlier resistant: single surprising responses don't wildly shift estimate
 * - Provides standard error as a first-class metric
 */

// 61-point grid from -3.0 to +3.0 in steps of 0.1
const GRID_SIZE = 61;
const GRID_MIN = -3.0;
const GRID_STEP = 0.1;
const THETA_GRID = Array.from({ length: GRID_SIZE }, (_, i) => GRID_MIN + i * GRID_STEP);

/**
 * Normal PDF for building prior density
 */
function normalPDF(x, mean, sd) {
    const z = (x - mean) / sd;
    return Math.exp(-0.5 * z * z) / (sd * Math.sqrt(2 * Math.PI));
}

/**
 * Rasch model probability: P(correct | theta, b)
 * Uses logit-scale difficulty
 */
export function raschProbability(theta, difficultyLogit) {
    const logit = theta - difficultyLogit;
    // Numerical stability: clamp the logit
    if (logit > 10) return 1.0 - 1e-10;
    if (logit < -10) return 1e-10;
    return 1.0 / (1.0 + Math.exp(-logit));
}

/**
 * Fisher information for a single item at a given theta
 * I(theta) = P(1-P), maximized when P = 0.5 (theta = difficulty)
 */
export function fisherInformation(theta, difficultyLogit) {
    const p = raschProbability(theta, difficultyLogit);
    return p * (1 - p);
}

/**
 * Convert author-assigned difficulty [0, 0.9] to logit scale [-3, +3]
 */
export function difficultyToLogit(authorDifficulty) {
    // Handle the 6.5 outlier case (already fixed in data, but defensive)
    const d = authorDifficulty > 1.0 ? authorDifficulty / 10.0 : authorDifficulty;
    return (d * 6.667) - 3.0;
}

/**
 * Build a prior density from stored mean and variance parameters
 * Returns array of density values at each grid point
 */
function buildDensity(mean, variance) {
    const sd = Math.sqrt(Math.max(variance, 0.01)); // floor variance to prevent collapse
    const density = THETA_GRID.map(t => normalPDF(t, mean, sd));
    // Normalize
    const area = density.reduce((sum, d) => sum + d, 0) * GRID_STEP;
    return density.map(d => d / area);
}

/**
 * Core Bayesian update: given a prior density, item difficulty, and response,
 * compute the posterior density and return EAP estimate.
 *
 * @param {number} priorMean - Current posterior mean
 * @param {number} priorVariance - Current posterior variance
 * @param {number} difficultyLogit - Item difficulty on logit scale
 * @param {boolean} isCorrect - Whether the response was correct
 * @returns {{ mean: number, variance: number, se: number }}
 */
export function updatePosterior(priorMean, priorVariance, difficultyLogit, isCorrect) {
    // Build prior density from stored parameters
    const prior = buildDensity(priorMean, priorVariance);

    // Compute likelihood at each grid point
    const likelihood = THETA_GRID.map(t => {
        const p = raschProbability(t, difficultyLogit);
        return isCorrect ? p : (1 - p);
    });

    // Unnormalized posterior = prior * likelihood
    const unnormalized = prior.map((p, i) => p * likelihood[i]);

    // Normalize
    const area = unnormalized.reduce((sum, v) => sum + v, 0) * GRID_STEP;
    if (area === 0) {
        // Fallback: if numerical underflow, return prior unchanged
        return { mean: priorMean, variance: priorVariance, se: Math.sqrt(priorVariance) };
    }
    const posterior = unnormalized.map(v => v / area);

    // Compute EAP (expected value)
    const mean = THETA_GRID.reduce((sum, t, i) => sum + t * posterior[i], 0) * GRID_STEP;

    // Compute posterior variance
    const variance = THETA_GRID.reduce((sum, t, i) => sum + (t - mean) ** 2 * posterior[i], 0) * GRID_STEP;

    return {
        mean,
        variance: Math.max(variance, 0.001), // floor to prevent numerical issues
        se: Math.sqrt(Math.max(variance, 0.001))
    };
}

/**
 * Process a response: update both global and domain-specific ability estimates
 *
 * @param {object} abilityEstimate - Current ability estimate object from Firestore
 * @param {string} domain - The domain of the item
 * @param {number} difficultyLogit - Item difficulty on logit scale
 * @param {boolean} isCorrect - Whether the response was correct
 * @returns {object} Updated ability estimate object
 */
export function processResponse(abilityEstimate, domain, difficultyLogit, isCorrect) {
    const updated = JSON.parse(JSON.stringify(abilityEstimate)); // deep clone

    // Update global estimate
    const globalResult = updatePosterior(
        updated.global.mean,
        updated.global.variance,
        difficultyLogit,
        isCorrect
    );
    updated.global.mean = globalResult.mean;
    updated.global.variance = globalResult.variance;
    updated.global.itemCount = (updated.global.itemCount || 0) + 1;

    // Update domain-specific estimate
    if (updated.byDomain && updated.byDomain[domain]) {
        const domainResult = updatePosterior(
            updated.byDomain[domain].mean,
            updated.byDomain[domain].variance,
            difficultyLogit,
            isCorrect
        );
        updated.byDomain[domain].mean = domainResult.mean;
        updated.byDomain[domain].variance = domainResult.variance;
        updated.byDomain[domain].itemCount = (updated.byDomain[domain].itemCount || 0) + 1;
    }

    return updated;
}

/**
 * Select the best item from a pool using Fisher information maximization
 * with domain-specific theta and exposure control.
 *
 * @param {Array} questionPool - Available questions
 * @param {Array} answeredIds - IDs of already-answered questions in this session
 * @param {object} abilityEstimate - Current ability estimate
 * @param {string} selectedMode - Current mode (for Quiz cap etc.)
 * @param {number} answeredCount - Number answered so far
 * @returns {object|null} The selected question, or null if none available
 */
export function selectNextItem(questionPool, answeredIds, abilityEstimate, selectedMode, answeredCount) {
    if (!questionPool || questionPool.length === 0) return null;

    const validQuestions = questionPool.filter(q => !answeredIds.includes(q.id));

    if (validQuestions.length === 0) return null;
    if (selectedMode === 'Quiz' && answeredCount >= 20) return null;

    // Score each item by Fisher information using domain-specific theta
    const scored = validQuestions.map(q => {
        const domain = q.domain;
        const theta = abilityEstimate?.byDomain?.[domain]?.mean ?? abilityEstimate?.global?.mean ?? 0;
        const diffLogit = q.difficulty_logit ?? difficultyToLogit(q.difficulty);
        const info = fisherInformation(theta, diffLogit);

        return { question: q, info };
    });

    // Sort by Fisher information descending (most informative first)
    scored.sort((a, b) => b.info - a.info);

    return scored[0].question;
}

/**
 * Classify a response into diagnostic quadrants based on correctness and response time
 *
 * @param {boolean} correct - Whether the answer was correct
 * @param {number} responseTimeMs - Response time in milliseconds
 * @param {number} difficulty - Item difficulty (author scale, 0-0.9)
 * @returns {string} "fluent" | "effortful" | "misconception" | "gap"
 */
export function classifyResponse(correct, responseTimeMs, difficulty) {
    // Heuristic median time based on difficulty (until we have empirical data)
    // Base 20s + 40s scaled by difficulty → easy items ~20s, hard items ~56s
    const estimatedMedianMs = (20000 + difficulty * 40000);
    const fastThreshold = estimatedMedianMs * 0.7;
    const slowThreshold = estimatedMedianMs * 1.5;

    if (correct && responseTimeMs < fastThreshold) return 'fluent';
    if (correct && responseTimeMs > slowThreshold) return 'effortful';
    if (!correct && responseTimeMs < fastThreshold) return 'misconception';
    if (!correct && responseTimeMs > slowThreshold) return 'gap';

    // Middle zone defaults
    return correct ? 'fluent' : 'gap';
}

/**
 * Create a default ability estimate for a new user or migration
 */
export function createDefaultAbilityEstimate() {
    const domainDefault = { mean: 0.0, variance: 1.0, itemCount: 0 };
    return {
        global: { mean: 0.0, variance: 1.0, itemCount: 0 },
        byDomain: {
            'General Security Concepts': { ...domainDefault },
            'Threats, Vulnerabilities and Mitigations': { ...domainDefault },
            'Security Architecture': { ...domainDefault },
            'Security Operations': { ...domainDefault },
            'Security Program Management': { ...domainDefault }
        }
    };
}

/**
 * Migrate an old-style ability value to the new EAP structure
 * @param {number} oldAbility - The old delta-rule ability value (-1 to +1)
 * @returns {object} New ability estimate structure
 */
export function migrateFromDeltaRule(oldAbility) {
    const estimate = createDefaultAbilityEstimate();
    // Map old ability to logit scale (old range was -1 to +1, new is -3 to +3)
    const mappedAbility = oldAbility * 1.5; // conservative scaling
    estimate.global.mean = mappedAbility;
    estimate.global.variance = 0.5; // moderate uncertainty since we have some data

    // Set domain estimates with higher uncertainty
    Object.keys(estimate.byDomain).forEach(domain => {
        estimate.byDomain[domain].mean = mappedAbility;
        estimate.byDomain[domain].variance = 0.8;
    });

    return estimate;
}
